
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCircleIcon } from './common/Icons';
import { Button } from './common/Button';
import type { User } from 'firebase/auth';

interface UserProfileProps {
    user: User | null;
    onLogin: (e: React.MouseEvent) => void;
    onSignup: (e: React.MouseEvent) => void;
    onLogout: () => void;
}

export const UserProfile: React.FC<UserProfileProps> = ({ user, onLogin, onSignup, onLogout }) => {
    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);
    
    if (!user) {
        return (
            <div className="flex items-center gap-4">
                <a
                    href="/login"
                    onClick={onLogin}
                    className="text-sm font-medium text-slate-400 hover:text-white transition-colors"
                >
                    Log In
                </a>
                <Button size="sm" onClick={onSignup}>
                    Sign Up
                </Button>
            </div>
        );
    }

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsDropdownOpen(prev => !prev)}
                className="flex items-center justify-center text-slate-400 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#0a0a0f] focus:ring-cyan-400 p-2 min-h-[44px] min-w-[44px]"
                aria-haspopup="true"
                aria-expanded={isDropdownOpen}
            >
                <span className="sr-only">Open user menu</span>
                <UserCircleIcon className="w-8 h-8 hover:text-white transition-colors" />
            </button>
            {isDropdownOpen && (
                <div
                    className="origin-top-right absolute right-0 mt-2 w-56 rounded-xl border border-white/10 bg-[#1a1a24] shadow-lg backdrop-blur-xl focus:outline-none z-50"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="menu-button"
                >
                    <div className="py-1" role="none">
                        <div className="px-4 py-3 border-b border-white/10">
                            <p className="text-sm font-semibold text-white" role="none">
                                {user.displayName || 'Amazon Ads Operator'}
                            </p>
                            <p className="text-sm text-slate-500 truncate" role="none">
                                {user.email ?? 'No email on record'}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setIsDropdownOpen(false);
                                navigate('/settings/profile');
                            }}
                            className="w-full text-left text-slate-300 block px-4 py-2 text-sm hover:bg-white/10 hover:text-white transition-colors"
                            role="menuitem"
                        >
                            Profile Settings
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setIsDropdownOpen(false);
                                navigate('/settings/subscription');
                            }}
                            className="w-full text-left text-slate-300 block px-4 py-2 text-sm hover:bg-white/10 hover:text-white transition-colors"
                            role="menuitem"
                        >
                            Subscription Management
                        </button>
                        <button
                            onClick={onLogout}
                            className="w-full text-left text-slate-300 block px-4 py-2 text-sm hover:bg-white/10 hover:text-white transition-colors"
                            role="menuitem"
                        >
                            Log Out
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
